import React from "react";
import { Image, Pressable, StyleSheet, Text, View } from "react-native";
import { router } from "expo-router";
import type { BusinessSearchDoc } from "@/lib/search/businessSearch";

type Props = {
  item: BusinessSearchDoc;
};

export default function SearchBusinessCard({ item }: Props) {
  const locationText = [item.areaName, item.cityName, item.countyName]
    .filter(Boolean)
    .join(", ");
  const slug = item.slug?.trim();

  return (
    <Pressable
      style={styles.card}
      onPress={() => {
        if (!slug) return;

        router.push({
          pathname: "/p/[slug]",
          params: { slug },
        });
      }}
      disabled={!slug}
    >
      <View style={styles.row}>
        {item.logoUrl ? (
          <Image source={{ uri: item.logoUrl }} style={styles.logo} />
        ) : (
          <View style={[styles.logo, styles.logoFallback]}>
            <Text style={styles.logoFallbackText}>
              {item.businessName?.charAt(0)?.toUpperCase() || "B"}
            </Text>
          </View>
        )}

        <View style={styles.content}>
          <View style={styles.headerRow}>
            <Text style={styles.name} numberOfLines={1}>
              {item.businessName}
            </Text>

            <View style={styles.badges}>
              {item.isVerified ? (
                <View style={styles.verifiedBadge}>
                  <Text style={styles.badgeText}>Verified</Text>
                </View>
              ) : null}

              {item.isFeatured ? (
                <View style={styles.featuredBadge}>
                  <Text style={styles.badgeText}>Featured</Text>
                </View>
              ) : null}
            </View>
          </View>

          {!!item.subcategoryName && (
            <Text style={styles.meta} numberOfLines={1}>
              {item.subcategoryName}
            </Text>
          )}

          {!item.subcategoryName && !!item.categoryName && (
            <Text style={styles.meta} numberOfLines={1}>
              {item.categoryName}
            </Text>
          )}

          {!!locationText && (
            <Text style={styles.location} numberOfLines={1}>
              {locationText}
            </Text>
          )}

          {!!item.shortDescription && (
            <Text style={styles.description} numberOfLines={2}>
              {item.shortDescription}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  row: {
    flexDirection: "row",
    gap: 12,
  },
  logo: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: "#f3f4f6",
  },
  logoFallback: {
    alignItems: "center",
    justifyContent: "center",
  },
  logoFallbackText: {
    fontSize: 24,
    fontWeight: "700",
    color: "#374151",
  },
  content: {
    flex: 1,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  name: {
    flex: 1,
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
  },
  badges: {
    flexDirection: "row",
    gap: 6,
  },
  verifiedBadge: {
    backgroundColor: "#dcfce7",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  featuredBadge: {
    backgroundColor: "#fef3c7",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#374151",
  },
  meta: {
    marginTop: 4,
    fontSize: 13,
    color: "#374151",
  },
  location: {
    marginTop: 4,
    fontSize: 13,
    color: "#6b7280",
  },
  description: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 18,
    color: "#4b5563",
  },
});
